import React, { useState, useCallback, useRef } from 'react';
import { Upload, Eye, Edit3, FileDown, Save, FileText } from 'lucide-react';
import { marked } from 'marked';
import api from '../../../services/api';

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true
});

const SAMPLE_MD = `# Oferta handlowa

## 1. Wprowadzenie

Szanowni Państwo,

Z przyjemnością przedstawiamy ofertę na realizację projektu **[nazwa projektu]**.

## 2. Zakres prac

### 2.1 Etap I — Przygotowanie
- Analiza wymagań
- Projekt graficzny
- Przygotowanie materiałów

### 2.2 Etap II — Realizacja
- Produkcja elementów
- Montaż
- Testy i odbiór

## 3. Harmonogram

| Etap | Termin | Uwagi |
|------|--------|-------|
| Przygotowanie | 2 tygodnie | Od daty akceptacji |
| Produkcja | 4 tygodnie | — |
| Montaż | 1 tydzień | Na miejscu |

## 4. Warunki

1. Płatność: 50% zaliczka, 50% po realizacji
2. Gwarancja: 24 miesiące
3. Ważność oferty: 30 dni

---

*Oferta przygotowana przez [firma]*
`;

export default function OfferContentEditor({ offer, onSave }) {
  const [content, setContent] = useState(offer.content_md || '');
  const [mode, setMode] = useState(content ? 'preview' : 'edit'); // edit | preview | split
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(true);
  const fileInputRef = useRef(null);

  const handleChange = useCallback((val) => {
    setContent(val);
    setSaved(false);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await api.put(`/offers/${offer.id}/content`, { content_md: content });
      setSaved(true);
      if (onSave) onSave(content);
    } catch (e) {
      alert('Błąd zapisu: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  }, [content, offer.id, onSave]);

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/offers/${offer.id}/content/upload`, formData);
      setContent(res.data.content_md);
      setSaved(true);
    } catch (err) {
      alert('Błąd uploadu: ' + (err.response?.data?.error || err.message));
    }
    e.target.value = '';
  }, [offer.id]);

  const handleInsertSample = useCallback(() => {
    if (content && !window.confirm('Wstawić przykładowy szablon? Obecna treść zostanie zastąpiona.')) return;
    setContent(SAMPLE_MD);
    setSaved(false);
  }, [content]);

  const renderHtml = useCallback(() => {
    try {
      return marked(content || '');
    } catch {
      return '<p style="color:red">Błąd renderowania markdown</p>';
    }
  }, [content]);

  const exportPDF = useCallback(() => {
    const html = renderHtml();
    const printHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Oferta ${offer.offer_number}</title>
      <style>
        @page { margin: 20mm; size: A4; }
        * { box-sizing: border-box; }
        body {
          font-family: 'Segoe UI', Arial, sans-serif;
          color: #1f2937;
          font-size: 13px;
          line-height: 1.7;
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .header {
          background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
          color: white;
          padding: 24px 32px;
          margin: -20mm -20mm 24px -20mm;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .header h1 { margin: 0; font-size: 22px; letter-spacing: 0.5px; }
        .header .meta { font-size: 12px; opacity: 0.8; }
        .parties {
          display: flex;
          gap: 24px;
          margin-bottom: 24px;
        }
        .party {
          flex: 1;
          border-radius: 8px;
          padding: 16px 18px;
        }
        .party-issuer { background: #f0f4ff; border: 1px solid #c7d2fe; }
        .party-client { background: #f8fafc; border: 1px solid #e2e8f0; }
        .party-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.8px; font-weight: 700; margin-bottom: 8px; }
        .party-issuer .party-label { color: #3b82f6; }
        .party-client .party-label { color: #64748b; }
        .party-name { font-size: 15px; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
        .party-detail { font-size: 13px; color: #475569; line-height: 1.6; }
        .content h1 { font-size: 22px; color: #1e3a5f; border-bottom: 2px solid #2563eb; padding-bottom: 8px; margin: 28px 0 16px 0; }
        .content h1:first-child { margin-top: 0; }
        .content h2 { font-size: 17px; color: #1e3a5f; margin: 24px 0 12px 0; border-left: 4px solid #2563eb; padding-left: 12px; }
        .content h3 { font-size: 14px; color: #374151; margin: 20px 0 8px 0; }
        .content p { margin: 8px 0; }
        .content ul, .content ol { margin: 8px 0; padding-left: 24px; }
        .content li { margin: 4px 0; }
        .content table { width: 100%; border-collapse: separate; border-spacing: 0; margin: 16px 0; border-radius: 8px; overflow: hidden; border: 1px solid #e2e8f0; }
        .content th { background: #1e3a5f; color: white; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; padding: 10px 14px; }
        .content td { padding: 8px 14px; border-bottom: 1px solid #f1f5f9; }
        .content tr:nth-child(even) { background: #f8fafc; }
        .content blockquote { border-left: 4px solid #2563eb; background: #f0f4ff; margin: 16px 0; padding: 12px 18px; border-radius: 0 8px 8px 0; color: #1e3a5f; }
        .content code { background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-size: 12px; }
        .content pre { background: #1e293b; color: #e2e8f0; padding: 16px; border-radius: 8px; overflow-x: auto; }
        .content pre code { background: none; padding: 0; color: inherit; }
        .content hr { border: none; border-top: 2px solid #e2e8f0; margin: 24px 0; }
        .content strong { color: #1e293b; }
        .content em { color: #475569; }
        .content a { color: #2563eb; text-decoration: none; }
        .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; text-align: center; }
      </style></head><body>
      <div class="header">
        <div>
          <div class="meta">Oferta</div>
          <h1>${offer.offer_number || ''}</h1>
        </div>
        <div style="text-align:right">
          <div class="meta">Data wystawienia</div>
          <div style="font-size:16px;font-weight:700">${new Date(offer.created_at).toLocaleDateString('pl-PL')}</div>
        </div>
      </div>
      <div class="parties">
        <div class="party party-issuer">
          <div class="party-label">Wystawca</div>
          <div class="party-name">${offer.issuer_name || ''}</div>
          <div class="party-detail">
            ${offer.issuer_nip ? 'NIP: ' + offer.issuer_nip + '<br>' : ''}
            ${[offer.issuer_street, offer.issuer_zip, offer.issuer_city].filter(Boolean).join(', ')}
            ${offer.issuer_email ? '<br>' + offer.issuer_email : ''}
            ${offer.issuer_phone ? '<br>tel. ' + offer.issuer_phone : ''}
          </div>
        </div>
        <div class="party party-client">
          <div class="party-label">Klient</div>
          <div class="party-name">${offer.company_name || offer.client_company || ''}</div>
          <div class="party-detail">
            ${offer.client_name ? offer.client_name + '<br>' : ''}
            ${offer.company_nip ? 'NIP: ' + offer.company_nip + '<br>' : ''}
            ${offer.company_address || ''}
            ${offer.company_email ? '<br>' + offer.company_email : ''}
            ${offer.company_phone ? '<br>tel. ' + offer.company_phone : ''}
          </div>
        </div>
      </div>
      <div class="content">${html}</div>
      <div class="footer">
        Oferta ${offer.offer_number} | ${new Date(offer.created_at).toLocaleDateString('pl-PL')}
        ${offer.valid_until ? ' | Ważna do: ' + new Date(offer.valid_until).toLocaleDateString('pl-PL') : ''}
      </div>
      </body></html>`;
    const win = window.open('', '_blank');
    win.document.write(printHtml);
    win.document.close();
    setTimeout(() => win.print(), 500);
  }, [renderHtml, offer]);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-3 bg-white border-b border-gray-200">
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button onClick={() => setMode('edit')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${mode === 'edit' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            <Edit3 size={14} className="inline mr-1" />Edytor
          </button>
          <button onClick={() => setMode('split')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${mode === 'split' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            Podziel
          </button>
          <button onClick={() => setMode('preview')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${mode === 'preview' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            <Eye size={14} className="inline mr-1" />Podgląd
          </button>
        </div>

        <div className="flex-1" />

        <button onClick={handleInsertSample} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition" title="Wstaw przykładowy szablon">
          <FileText size={14} className="inline mr-1" />Szablon
        </button>

        <input ref={fileInputRef} type="file" accept=".md,.txt" onChange={handleUpload} className="hidden" />
        <button onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition" title="Wgraj plik .md">
          <Upload size={14} className="inline mr-1" />Wgraj .md
        </button>

        <button onClick={handleSave} disabled={saving || saved} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${saved ? 'bg-gray-100 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
          <Save size={14} className="inline mr-1" />{saving ? 'Zapisuję...' : saved ? 'Zapisano' : 'Zapisz'}
        </button>

        <button onClick={exportPDF} disabled={!content} className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50">
          <FileDown size={14} className="inline mr-1" />Eksport PDF
        </button>
      </div>

      {/* Editor / Preview */}
      <div className="flex-1 flex overflow-hidden">
        {(mode === 'edit' || mode === 'split') && (
          <div className={`${mode === 'split' ? 'w-1/2 border-r border-gray-200' : 'w-full'} flex flex-col`}>
            <textarea
              value={content}
              onChange={(e) => handleChange(e.target.value)}
              placeholder="Wpisz treść oferty w formacie Markdown lub wgraj plik .md..."
              className="flex-1 w-full p-4 font-mono text-sm text-gray-800 bg-gray-50 resize-none focus:outline-none focus:bg-white"
              spellCheck={false}
            />
          </div>
        )}

        {(mode === 'preview' || mode === 'split') && (
          <div className={`${mode === 'split' ? 'w-1/2' : 'w-full'} overflow-auto bg-white`}>
            {content ? (
              <div
                className="p-6 prose prose-sm max-w-none
                  prose-headings:text-[#1e3a5f] prose-h1:text-xl prose-h1:border-b-2 prose-h1:border-blue-600 prose-h1:pb-2
                  prose-h2:text-base prose-h2:border-l-4 prose-h2:border-blue-600 prose-h2:pl-3
                  prose-table:border prose-table:border-gray-200 prose-table:rounded-lg
                  prose-th:bg-[#1e3a5f] prose-th:text-white prose-th:text-xs prose-th:uppercase prose-th:tracking-wide prose-th:font-semibold prose-th:px-3 prose-th:py-2
                  prose-td:px-3 prose-td:py-2 prose-td:border-b prose-td:border-gray-100
                  prose-blockquote:border-l-blue-600 prose-blockquote:bg-blue-50
                  prose-a:text-blue-600 prose-strong:text-gray-900"
                dangerouslySetInnerHTML={{ __html: renderHtml() }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <FileText size={48} className="mb-3 opacity-50" />
                <p className="text-sm">Brak treści — przejdź do edytora lub wgraj plik .md</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
